import React, { MouseEvent, useCallback, useContext, useEffect, useState } from "react";

import "../menubar.css";

interface IInnerMenuBarContext {
    children: React.ReactNode;
    setChildren: (children: React.ReactNode) => void;
}

const DEFAULT_CONTEXT = { children: [], setChildren: () => { } };
const InnerMenuBarContext = React.createContext<IInnerMenuBarContext>(DEFAULT_CONTEXT);

const InnerMenuBarProvider: React.FC = function (props) {
    const [state, setState] = useState<IInnerMenuBarContext>(DEFAULT_CONTEXT);

    const setChildren = useCallback((children) => {
        setState({
            children: children,
            setChildren: setChildren
        });
    }, [setState]);

    // Update children once to set the setChildren() function
    useEffect(() => {
        setChildren([]);
    }, [setChildren]);

    return <InnerMenuBarContext.Provider value={state}>
        {props.children}
    </InnerMenuBarContext.Provider>
}

const InnerMenuBarHost: React.FC = function (props) {
    const menuContext = useContext(InnerMenuBarContext);

    if (!menuContext.children || (Array.isArray(menuContext.children) && menuContext.children.length === 0)) {
        return null;
    }

    return <div className="inner-menubar">
        {menuContext.children}
    </div>;
}

interface IInnerMenuBarButtonProps {
    icon?: string;
    accentIcon?: string;
    title?: string;
    disabled?: boolean;
    onClick?: (e: MouseEvent) => void;
}

const InnerMenuBarButton: React.FC<IInnerMenuBarButtonProps> = function (props) {
    return <button type="button" className="menuitem" title={props.title} disabled={props.disabled} onClick={props.onClick}>
        {props.accentIcon && props.icon
            ? <div className="stack">
                <i className={`icon-${props.icon}`} />
                <i className={`icon-${props.accentIcon} stack-icon-br`} />
            </div>
            : props.icon
                ? <i className={`icon-${props.icon}`} />
                : null}
        {props.children}
    </button>;
}


type InnerMenuBarComponent = React.FC & {
    Host: typeof InnerMenuBarHost;
    Provider: typeof InnerMenuBarProvider;
    Button: typeof InnerMenuBarButton;
};

const InnerMenuBar: InnerMenuBarComponent = function (props) {
    const { setChildren } = useContext(InnerMenuBarContext);

    useEffect(() => {
        setChildren(props.children);

        return function () {
            setChildren(null);
        }
    }, [props.children, setChildren]);

    return null;
}

InnerMenuBar.Host = InnerMenuBarHost;
InnerMenuBar.Provider = InnerMenuBarProvider;
InnerMenuBar.Button = InnerMenuBarButton;

export default InnerMenuBar;
